import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Nav, NavDropdown, Navbar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { signOut } from '../../../actions/auth.actions';
import { addFlashMessage } from '../../../actions/flashMessages.actions';
import logo from '../../../images/blue_logo.svg';
import christmasLogo from '../../../../assets/images/redesign/new-logo-christmas.png';
import axios from '../../../utils/axiosConfig';
import useBreakpoint from '../../hooks/useBreakpoint';
import Divider from '../Divider';
import CustomNavDropdown from '../Nav/NavDropdown';
import NavLink from '../Nav/NavLink';
import './Header.scss';
import LanguageSelectorSlideOver from './LanguageSelectorSlideOver';

const Header = () => {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  /** @type {import('../../../reducers/auth.reducer').AuthStore} */
  const auth = useSelector(state => state.auth);
  /** @type {import('../../../reducers/auth.reducer').User} */
  const user = get(auth, 'user') || {};

  const basename = window.location.pathname;
  const internalView = (['/internal'].some(path => basename.match(path)));

  const MANAGER_LINKS = useMemo(() => [
    { title: 'influencers.header.task_manager', link: '/manager', show: true },
    { title: 'influencers.header.lab', link: '/manager/lab', show: true },
    { title: 'influencers.header.network', link: '/manager/network', show: user.admin && !user.restricted_admin },
    { title: 'influencers.header.brands', link: '/manager/accounts', show: true },
    { title: 'influencers.header.audiences', link: '/manager/audiences', show: true },
    { title: 'influencers.header.all_campaigns', link: '/manager/campaigns?scope=all', show: true },
    { title: 'influencers.header.reporting_offer_groups', link: '/manager/reporting_offer_groups', show: !user.restricted_admin },
    { title: 'influencers.header.campaign_groups', link: '/manager/campaign_groups', show: !user.restricted_admin },
    { title: 'influencers.header.fulfillment', link: '/manager/fulfillment', show: true },
    { title: 'influencers.header.expected_fulfillment', link: '/manager/expected_fulfillments', show: !user.restricted_admin },
    { title: 'influencers.header.my_purchased_media_rights', link: '/manager/purchased_media_rights', show: !user.restricted_admin },
    { title: 'influencers.header.brand_dashboard_users', link: '/manager/agents', show: true },
    { title: 'influencers.header.mail', link: '/admins/mails', show: !user.restricted_admin },
    { title: 'influencers.header.predictive', link: '/manager/predictions', show: true },
    { title: 'influencers.header.collectives', link: '/internal/collectives', show: true },
  ], [user]);

  const ADMIN_LINKS = useMemo(() => [
    { title: 'influencers.header.dashboard', link: '/admins/dashboard', show: user.super_admin || user.analytics_admin },
    { title: 'influencers.header.custom_reports', link: '/admins/custom_reports/categories', show: user.admin },
    { title: 'influencers.header.latest_posts', link: '/admins/posts', show: true },
    { title: 'influencers.header.partner_accounts', link: '/admins/partner_accounts', show: !user.restricted_admin },
    { title: 'influencers.header.influencers', link: '/manager/users', show: true },
    { title: 'influencers.header.suspensions', link: '/admins/suspensions', show: !user.restricted_admin },
    { title: 'influencers.header.dashboard_invoices', link: '/admins/invoices', show: user.super_admin },
    { title: 'influencers.header.lumanu_payments', link: '/internal/lumanu-payments', show: true },
    { title: 'influencers.header.tags', link: '/admins/tags', show: true },
    { title: 'influencers.header.contracts', link: '/admins/contracts', show: user.admin && !user.restricted_admin },
    { title: 'influencers.header.admin_management', link: '/admins/user_admin_managements', show: (user.super_admin || user.user_management_admin) && !user.restricted_admin },
    { title: 'influencers.header.access_grants', link: '/admins/access_grants', show: user.admin && !user.restricted_admin },
  ], [user]);

  const handleLogout = useCallback(() => {
    onToggleNavbar(false);
    axios.delete('/api/v1/influencers/users/sign_out')
      .then(() => {
        signOut(dispatch);
        document.location = '/influencers/signin';
      }).catch(() => {
        dispatch(addFlashMessage({
          text: t('influencers.common.message_error'),
          type: 'error'
        }));
      });
  }, [history, signOut]);

  const linksDesktop = useMemo(() => [{
    eventKey: "/",
    to: "/",
    text: 'influencers.header.home',
    show: breakpoint !== 'xs',
  }, {
    eventKey: "/learning-center",
    to: "/learning-center",
    text: 'influencers.header.learning_center',
    show: !internalView
  }, {
    eventKey: "/analytics",
    to: "/analytics",
    text: 'influencers.header.analytics',
    show: !internalView
  }, {
    eventKey: "/settings",
    to: "/settings",
    icon: <i className="fal fa-cog" />,
    text: 'influencers.header.settings',
    show: breakpoint === 'xs'
  }, {
    eventKey: "manager",
    text: 'influencers.header.manager',
    dropdown: MANAGER_LINKS,
    show: user.partner_manager || user.admin
  }, {
    eventKey: "admin",
    text: 'influencers.header.admin',
    dropdown: ADMIN_LINKS,
    show: user.admin
  }, {
    eventKey: "/styles",
    to: "/styles",
    icon: <i className="fal fa-paint-brush" />,
    text: 'influencers.header.styles',
    show: user.admin
  }, {
    eventKey: "/profile",
    to: "/profile",
    icon: <i className="fal fa-user" />,
    text: 'influencers.header.profile',
    show: breakpoint !== 'xs'
  }], [breakpoint, user.admin]);

  const [state, setState] = useState({
    loading: false,
    defaultPath: '/',
    expandedNavbar: false,
    currentLogo: logo
  });

  useEffect(() => {
    if (get(auth, 'user.id')) {
      setState({
        ...state,
        loading: true,
      });
    }
  }, [auth.user]);

  useEffect(() => {
    if (get(auth, 'user.id')) {
      setState({
        ...state,
        loading: true,
      });
      axios.get(`/api/v1/feature_flags/${encodeURIComponent('Christmas Logo')}`)
        .then(({ data }) => {
          setState({
            ...state,
            currentLogo: data.enabled ? christmasLogo : logo,
            loading: false,
          });
        }).catch(_ => setState({
          ...state,
          loading: false,
        }));
    }
  }, [auth.user]);

  useEffect(() => {
    const defaultPath = `/${location.pathname.split('/')[1]}`;
    setState({
      ...state,
      defaultPath
    });
  }, [location.pathname]);

  const renderNavItem = useCallback((item) => {
    if (!item.show) return null;

    if (item.dropdown) {
      return <NavDropdown
        key={item.eventKey}
        id={`navdropdown-${item.eventKey}`}
        className="custom-nav-dropdown no-underline"
        title={t(item.text)}
        alignRight>
        {
          item.dropdown
            .map(elem => (
              elem.show && <NavDropdown.Item
                key={elem.link}
                to={elem.link}
                href={elem.link}
                className='text-capitalize'
              >
                {t(elem.title)}
              </NavDropdown.Item>
            ))
        }
      </NavDropdown>;
    }

    return <NavLink
      key={item.eventKey}
      eventKey={item.eventKey}
      as={Link}
      to={item.to}
      badge={item.badge}
      icon={item.icon}
      text={t(item.text)}
    >
    </NavLink>;
  }, []);

  const renderNavItemMobile = useCallback((item) => {
    if (!item.show) return null;

    if (item.dropdown) {
      return <CustomNavDropdown
        key={item.eventKey}
        id={`navdropdown-${item.eventKey}`}
        className="custom-nav-dropdown"
        title={t(item.text)}>
        {
          item.dropdown.map(elem => (
            elem.show && <NavDropdown.Item
              key={elem.link}
              to={elem.link}
              href={elem.link}
              className='text-capitalize'
            >
              {t(elem.title)}
            </NavDropdown.Item>
          ))
        }
      </CustomNavDropdown>;
    }

    return <NavLink
      key={item.eventKey}
      eventKey={item.eventKey}
      as={Link}
      to={item.to}
      badge={item.badge}
      icon={item.icon}
      text={t(item.text)}>
    </NavLink>;
  }, []);

  const renderDesktopNavbar = () => {

    if (auth.user && !state.loading) {
      return <React.Fragment>
        <Nav className="nav-no-collapsible" />

        <Nav activeKey={state.defaultPath}>
          {/* Add option to render dynamic dropdowns */}
          {linksDesktop.map(renderNavItem)}
          <NavDropdown
            id="navdropdown-settings"
            className="custom-nav-dropdown icon-only"
            title={<i className="fal fa-cog" />}
            alignRight>
            <NavDropdown.Item to="/settings" as={Link}>
              {t('influencers.header.settings')}
            </NavDropdown.Item>
            <NavDropdown.Item onClick={handleLogout} >
              {t('influencers.header.sign_out')}
            </NavDropdown.Item>
          </NavDropdown>

          <LanguageSelectorSlideOver />
        </Nav>
      </React.Fragment>;
    }
    return null;
  };

  const onToggleNavbar = (expanded) => {
    setState({
      ...state,
      expandedNavbar: expanded
    });
  };

  const renderMobileNavbar = () => {
    if (auth.user && !state.loading) {
      return <React.Fragment>
        <Navbar.Collapse id="navbarHeaderMobile">
          <Nav activeKey={state.defaultPath} >
            {linksDesktop.map(renderNavItemMobile)}
            <NavLink onClick={handleLogout} text={t('influencers.header.sign_out')} />

            <LanguageSelectorSlideOver />
          </Nav>
          <Divider className="m-0" />
        </Navbar.Collapse>
        <Divider />
      </React.Fragment>;
    }
    // No auth header
    return null;
  };

  return (
    <header className="header">
      <Navbar collapseOnSelect
        expand="sm" fixed="top" bg="light"
        onToggle={onToggleNavbar}
        expanded={state.expandedNavbar}>
        <div className='navbar-wrapper'>
          <Navbar.Brand to="/" as={Link}>
            <img className="header-logo" src={state.currentLogo} alt="Obviously logo" />
          </Navbar.Brand>
          {breakpoint !== 'xs' && renderDesktopNavbar()}

          {breakpoint === 'xs'
            && auth.user && !state.loading &&
            <Nav className="nav-no-collapsible"
              activeKey={state.defaultPath}>
              <React.Fragment>
                <Nav.Link
                  className="icon"
                  to="/"
                  as={Link}
                  eventKey="/home">
                  <i className="fal fa-home"></i>
                </Nav.Link>
                <Nav.Link
                  className="icon"
                  to="/profile"
                  as={Link}
                  eventKey="/profile">
                  <i className="fal fa-user"></i>
                </Nav.Link>
              </React.Fragment>

              <Navbar.Toggle aria-controls="navbarHeaderMobile">
                <span>
                  <i className="fal fa-bars" />
                </span>
              </Navbar.Toggle>
            </Nav>
          }
          {!auth.user &&
            <React.Fragment>
              <Nav className="nav-no-collapsible" />

              <Nav className="align-items-end">
                <LanguageSelectorSlideOver />
              </Nav>
            </React.Fragment>
          }

        </div>
        {breakpoint === 'xs' && renderMobileNavbar()}
      </Navbar>

    </header >
  );
};

export default Header;
